import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { InventoryLevel, StockLocation } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { emptyAdjustForm, toAdjustBody } from '../api/mappers';
import { useAdjustStock } from '../hooks/use-inventory-mutations';
import {
  adjustStockSchema,
  type AdjustStockFormInput,
  type AdjustStockFormOutput,
} from '../schemas/adjust-stock-schema';
import { ADJUST_KINDS, insufficientStock, projectedOnHand } from '../schemas/inventory-rules';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['kind', 'quantity', 'reason']);
const REASON_PRESETS = [
  'correction',
  'stocktake',
  'received',
  'damaged',
  'theft',
  'returned',
  'promotion',
] as const;

export interface AdjustStockDialogProps {
  storeId: string;
  level: InventoryLevel;
  location: StockLocation | undefined;
  /** Given on the variant page: a picker that swaps the level (the page remounts the dialog). */
  locationOptions?: ReadonlyArray<{ id: string; name: string }>;
  onLocationChange?: (locationId: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The three ways stock changes by hand; every one is written to the ledger with its reason. */
export function AdjustStockDialog({
  storeId,
  level,
  location,
  locationOptions,
  onLocationChange,
  open,
  onOpenChange,
}: AdjustStockDialogProps) {
  const { t } = useTranslation(['inventory', 'common']);
  const message = useErrorMessage();
  const adjust = useAdjustStock(storeId);
  const form = useForm<AdjustStockFormInput, unknown, AdjustStockFormOutput>({
    resolver: useI18nResolver(adjustStockSchema(level.onHand)),
    defaultValues: emptyAdjustForm(),
  });
  const kind = useWatch({ control: form.control, name: 'kind' });
  const quantity = useWatch({ control: form.control, name: 'quantity' });
  const typed = /^[+-]?\d+$/.test(String(quantity).trim()) ? Number(quantity) : null;
  const after = typed === null ? null : projectedOnHand(level.onHand, kind, typed);
  const availableAfter = after === null ? null : after - level.reserved;
  // A count is a fact, so the API allows it; the dialog only warns.
  const belowReserved =
    availableAfter !== null && after !== null && after >= 0 && availableAfter < 0;

  const close = () => {
    onOpenChange(false);
    adjust.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      await adjust.mutateAsync(toAdjustBody(values, level.variantId, level.locationId));
      close();
    } catch (error) {
      const short = insufficientStock(error);
      if (short) {
        form.setError('root.server', {
          type: 'server',
          message:
            short.onHand === null
              ? t('inventory:adjust.errors.insufficient')
              : t('inventory:adjust.errors.insufficientOnHand', { onHand: short.onHand }),
        });
        return;
      }
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('inventory:adjust.title')}</DialogTitle>
          <DialogDescription>
            {t('inventory:adjust.description', {
              product: level.productTitle,
              variant: level.variantTitle,
              location: location?.name ?? '',
            })}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            {locationOptions && locationOptions.length > 1 && onLocationChange ? (
              <div className="grid gap-1.5">
                <Label htmlFor="adjust-location">{t('inventory:adjust.location')}</Label>
                <Select value={level.locationId} onValueChange={onLocationChange}>
                  <SelectTrigger id="adjust-location" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {locationOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <dl className="bg-muted/40 grid grid-cols-2 gap-2 rounded-md border p-3 text-sm sm:grid-cols-4">
              <div>
                <dt className="text-muted-foreground text-xs">{t('inventory:columns.onHand')}</dt>
                <dd className="tabular-nums" data-testid="adjust-on-hand">
                  {level.onHand}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">{t('inventory:columns.reserved')}</dt>
                <dd className="tabular-nums">{level.reserved}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">{t('inventory:adjust.after')}</dt>
                <dd className="tabular-nums" data-testid="adjust-after">
                  {after ?? '—'}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">
                  {t('inventory:adjust.availableAfter')}
                </dt>
                <dd className="tabular-nums" data-testid="adjust-available-after">
                  {availableAfter ?? '—'}
                </dd>
              </div>
            </dl>
            {belowReserved ? (
              <Alert role="status">
                <AlertDescription>
                  {t('inventory:adjust.belowReserved', { available: availableAfter })}
                </AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="kind"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('inventory:adjust.kind')}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={(value) => field.onChange(value)}
                      className="gap-2"
                    >
                      {ADJUST_KINDS.map((option) => (
                        <div key={option} className="flex items-start gap-3 rounded-md border p-3">
                          <RadioGroupItem
                            value={option}
                            id={`adjust-kind-${option}`}
                            aria-describedby={`adjust-kind-${option}-hint`}
                            className="mt-0.5"
                          />
                          <div className="flex flex-col gap-0.5">
                            <Label htmlFor={`adjust-kind-${option}`}>
                              {t(`inventory:adjust.kinds.${option}.label`)}
                            </Label>
                            <p
                              id={`adjust-kind-${option}-hint`}
                              className="text-muted-foreground text-xs"
                            >
                              {t(`inventory:adjust.kinds.${option}.hint`)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="quantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t(`inventory:adjust.kinds.${kind}.quantity`)}</FormLabel>
                  <FormControl>
                    <Input inputMode="numeric" autoComplete="off" {...field} />
                  </FormControl>
                  <FormDescription>
                    {t(`inventory:adjust.kinds.${kind}.quantityHint`)}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('inventory:adjust.reason')}</FormLabel>
                  <div
                    role="group"
                    aria-label={t('inventory:adjust.presets.label')}
                    className="flex flex-wrap gap-1.5"
                  >
                    {REASON_PRESETS.map((preset) => {
                      const text = t(`inventory:adjust.presets.${preset}`);
                      return (
                        <Button
                          key={preset}
                          type="button"
                          size="sm"
                          variant={field.value === text ? 'secondary' : 'outline'}
                          className="h-7 rounded-full px-2.5 text-xs"
                          onClick={() =>
                            form.setValue('reason', text, {
                              shouldDirty: true,
                              shouldValidate: true,
                            })
                          }
                        >
                          {text}
                        </Button>
                      );
                    })}
                  </div>
                  <FormControl>
                    <Textarea
                      rows={2}
                      placeholder={t('inventory:adjust.reasonPlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={adjust.isPending}>
                {adjust.isPending ? t('common:actions.saving') : t('inventory:adjust.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
